import React, { useContext, useState } from "react"
import { useQuery } from "react-query"

import Badge from "src/components/Badge"
import DataTable from "src/components/DataTable"
import Form from "src/components/Form"
import OverflowMenu from "src/components/OverflowMenu"

import { queryInvoiceItems } from "src/api/Apideck"
import { queryProducts } from "src/api/Billing/Items"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import { AccountingContext } from "./AccountingView"

const BLANK_LOADING_ROW_COUNT = 5
const MAPPED = "Mapped"
const UNMAPPED = "Unmapped"
const PAGE_SIZE = 25

const ItemMappingTab = () => {
  const { integration, marinaSlug, selectedAccountingService } =
    useContext(AccountingContext)
  const urlParams = new URLSearchParams(window.location.search)
  const [currentPage, setCurrentPage] = useState(urlParams.get("page"))

  const handlePageChange = (page) => {
    setCurrentPage(page.selected)
    updateUrlQueryParams({ page: page.selected })
  }

  const COLDEFS = [
    {
      key: "product_id",
      header: "Status",
      render: (data) => (
        <Badge
          color={data.productId ? "teal" : "gray"}
          text={data.productId ? MAPPED : UNMAPPED}
        />
      ),
      sortable: false,
    },
    {
      key: "accounting_item",
      header: "Product",
      render: (data) => data.accountingItem.name,
      sortable: false,
    },
    {
      key: "accounting_item",
      header: "Category",
      render: (data) => data.accountingItem.income_account.name,
      sortable: false,
    },
    {
      key: "price",
      header: "Price",
      render: (data) =>
        formatCurrencyFromMinorUnits({
          amount: data.accountingItem.unit_price,
        }),
      sortable: false,
    },
    {
      key: "accounting_item",
      header: "Accounting Item",
      render: (data) => (
        <OverflowMenu
          menuButtonVariant="quarternary"
          menuButtonLabel={data.productId ? data.productName : "Select"}
          menuItemWidth="450px"
          menuButtonFullWidth
          variant="tableRow"
        >
          <div className="p-2">
            <Form.IconTextField
              icon={<i className="icon icon-search-fa text-gray-600" />}
              position="left"
              id="productId"
              value={data.productName}
              isClearable
              placeholder="Search items"
            />
          </div>
          <div className="space-y-2">
            {dockwaProducts?.map((product) => {
              return (
                <option
                  key={product.id}
                  value={product.id}
                  className="cursor-pointer overflow-hidden truncate p-3 hover:bg-gray-200"
                >
                  {product.name}
                </option>
              )
            })}
          </div>
        </OverflowMenu>
      ),
      sortable: false,
    },
  ]

  const { isFetching: isLoadingInvoiceItems, data: invoiceItemData } = useQuery(
    {
      queryKey: ["invoice_items", marinaSlug, currentPage],
      queryFn: () => {
        return queryInvoiceItems({
          marinaSlug,
          page: currentPage,
          pageSize: PAGE_SIZE,
        })
      },
      refetchOnWindowFocus: false,
      enabled: !!integration?.setupComplete,
      retry: false,
    }
  )

  const { isFetching: isLoadingDockwaProducts, data: dockwaProducts } =
    useQuery({
      queryKey: ["products", marinaSlug],
      queryFn: () => queryProducts({ marinaSlug, page: 1 }),
      refetchOnWindowFocus: false,
      enabled: !!integration?.setupComplete,
      retry: false,
    })

  return (
    <div>
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Item Mapping</h4>
      <div className="mb-8">
        <span className="text-gray-800">
          Map your {selectedAccountingService?.name || "Accounting System"}{" "}
          items to Dockwa products.
        </span>
      </div>
      <div className="my-8">
        <DataTable
          name="invoice-items"
          isLoading={isLoadingInvoiceItems || isLoadingDockwaProducts}
          rowCount={BLANK_LOADING_ROW_COUNT}
          colDefs={COLDEFS}
          rowData={invoiceItemData?.items}
          page={invoiceItemData?.page}
          numberOfPages={invoiceItemData?.page_count}
          onPageChange={handlePageChange}
          fullHeight
          showBlankRows
          hideActions
          hideSettings
          pagination
        />
      </div>
    </div>
  )
}

export default ItemMappingTab
